/**
 * Entrada e primeira configuracao.
 *
 * KPI: instalada e a funcionar em menos de 2 minutos.
 *
 * Decisoes que servem esse objetivo:
 *  - tres passos, uma tela de cada vez, com um so botao grande em cada;
 *  - **todos os passos podem ser saltados**. Nada aqui bloqueia o caminho ate
 *    um aplicativo utilizavel — quem ficar preso no passo 2 nunca chega ao 3;
 *  - a permissao de avisos e pedida no momento em que faz sentido (depois de
 *    haver familia), nao a porta de entrada, onde toda a gente diz que nao.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Banner,
  BigButton,
  Card,
  Screen,
  Stack,
  TextField,
} from '@flos/ui';
import { createPermissionSet, ulid, type FamilyRole, type SessionInfo } from '@flos/shared';
import { useApp } from '../state/AppContext';
import { writeStoredSession } from '../wiring/sessionStore';
import { readServerUrl } from '../wiring/syncEngine';
import { isWellFormedInviteCode, normalizeInviteCode, redeemInvite } from '../wiring/invites';
import { SHELL_PATHS } from '../navigation';

type Step = 'perfil' | 'familia' | 'avisos' | 'pronto';

export function OnboardingScreen() {
  const { boot } = useApp();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('perfil');
  const [name, setName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);

  /** Grava a sessao e aplica-a de imediato — sem reiniciar o aplicativo. */
  const startSession = (
    displayName: string,
    familyId: string,
    role: FamilyRole,
    memberId = ulid(),
  ): void => {
    const info: SessionInfo = {
      memberId,
      familyId,
      displayName: displayName.trim() || 'Eu',
      role,
    };
    const permissions = createPermissionSet(role);
    writeStoredSession({ info, permissions });
    boot.core.session.set(info, permissions);
  };

  const finishProfile = (): void => {
    if (name.trim().length === 0) {
      setError('Escreva o seu nome, ou toque em “Saltar” para o fazer depois.');
      return;
    }
    setError(null);
    setStep('familia');
  };

  const createFamily = (): void => {
    startSession(name, ulid(), 'owner');
    setStep('avisos');
  };

  /**
   * Entrar numa familia que ja existe.
   *
   * O codigo tem de ser trocado com o servidor: **um codigo de convite nao e o
   * identificador da familia**. Enquanto isto foi tratado localmente, o codigo
   * era usado como `familyId` e tudo o que ela escrevesse a seguir ficava numa
   * familia inexistente, sem forma de reconciliar. Parecia funcionar, o que e
   * pior do que nao funcionar.
   *
   * E o unico passo do aplicativo que precisa de rede, e continua a poder ser
   * saltado: quem nao tiver ligacao comeca a sua propria familia agora e entra
   * na outra quando puder.
   */
  const joinFamily = async (): Promise<void> => {
    const code = normalizeInviteCode(inviteCode);
    if (!isWellFormedInviteCode(code)) {
      setError('O código de convite tem oito letras e números. O traço não conta.');
      return;
    }

    setJoining(true);
    setError(null);
    try {
      const result = await redeemInvite({
        url: readServerUrl(boot.core),
        getToken: () => boot.auth.getIdToken(),
        code,
        displayName: name,
      });

      if (!result.ok) {
        setError(result.message);
        return;
      }

      // O papel, a familia e o membro vem todos do servidor. Nada disto e
      // adivinhado aqui.
      startSession(result.displayName, result.familyId, result.role, result.memberId);
      // Sem isto, o transporte continuaria a sincronizar contra a familia
      // anterior — ou contra nenhuma.
      await boot.auth.useFamily(result.familyId, result.memberId);
      setStep('avisos');
    } catch (falha: unknown) {
      // `redeemInvite` nao lança; isto cobre o `useFamily` e qualquer surpresa.
      // Uma excecao no primeiro minuto de uso seria uma tela branca.
      boot.core.logger.error('Falhou a entrada por convite', {
        erro: falha instanceof Error ? falha.message : String(falha),
      });
      setError('Não consegui entrar na família agora. Tente outra vez daqui a pouco.');
    } finally {
      setJoining(false);
    }
  };

  const askNotifications = async (): Promise<void> => {
    if (typeof Notification !== 'undefined') {
      try {
        await Notification.requestPermission();
      } catch {
        // Recusar avisos nao pode impedir ninguem de usar o aplicativo.
      }
    }
    setStep('pronto');
  };

  const done = (): void => {
    navigate(SHELL_PATHS.HOME, { replace: true });
  };

  return (
    <Screen title="Bem-vinda">
      <Stack gap="lg">
        {error ? (
          <Banner tone="danger" title="Falta uma coisa" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        ) : null}

        {step === 'perfil' ? (
          <Card title="Como se chama?" icon="🙂">
            <Stack gap="md">
              <p>É só para o aplicativo a saber tratar pelo nome.</p>
              <TextField label="O seu nome" value={name} onChange={setName} autoFocus />
              <BigButton size="xl" icon="➡️" fullWidth onClick={finishProfile}>
                Continuar
              </BigButton>
              <BigButton variant="ghost" fullWidth onClick={() => setStep('familia')}>
                Saltar
              </BigButton>
            </Stack>
          </Card>
        ) : null}

        {step === 'familia' ? (
          <Card title="A sua família" icon="🏡">
            <Stack gap="md">
              <p>
                Pode começar sozinha e convidar as pessoas depois, ou entrar numa
                família que já exista.
              </p>
              <BigButton size="xl" icon="✨" fullWidth onClick={createFamily}>
                Começar a minha família
              </BigButton>
              <TextField
                label="Ou escreva o código de convite"
                value={inviteCode}
                onChange={setInviteCode}
                hint="São oito letras e números que alguém da família lhe deu. Para isto preciso de internet."
              />
              <BigButton
                variant="secondary"
                icon="🔑"
                fullWidth
                loading={joining}
                onClick={() => void joinFamily()}
              >
                Entrar com o código
              </BigButton>
              <BigButton variant="ghost" fullWidth onClick={() => setStep('avisos')}>
                Saltar
              </BigButton>
            </Stack>
          </Card>
        ) : null}

        {step === 'avisos' ? (
          <Card title="Quer ser avisada?" icon="🔔">
            <Stack gap="md">
              <p>
                Serve para lhe lembrar os remédios na hora certa e avisar quando
                algo precisa da sua atenção. Pode desligar quando quiser.
              </p>
              <BigButton size="xl" icon="🔔" fullWidth onClick={() => void askNotifications()}>
                Sim, avisem-me
              </BigButton>
              <BigButton variant="ghost" fullWidth onClick={() => setStep('pronto')}>
                Agora não
              </BigButton>
            </Stack>
          </Card>
        ) : null}

        {step === 'pronto' ? (
          <Card title="Está pronto" icon="🎉">
            <Stack gap="md">
              <p>Já pode usar o aplicativo. Toque no microfone e diga o que precisa.</p>
              <BigButton size="xl" icon="🏠" fullWidth onClick={done}>
                Ir para o início
              </BigButton>
            </Stack>
          </Card>
        ) : null}

        {step !== 'pronto' ? (
          <BigButton variant="ghost" fullWidth onClick={done}>
            Ver o aplicativo primeiro
          </BigButton>
        ) : null}
      </Stack>
    </Screen>
  );
}

export default OnboardingScreen;
